import keys from 'lodash/keys'
import isEmpty from 'lodash/isEmpty'
import isFunction from 'lodash/isFunction'
import isString from 'lodash/isString'
import isPlainObject from 'lodash/isPlainObject'
import isObject from 'lodash/isObject'
import flatMapDeep from 'lodash/flatMapDeep'
// TODO it seems we can move all query logic to API
import { buildQueryParams } from './common/utils/queryParams'
import { tokenService } from '@app/auth/tokenService'
import { contextOriginService } from '@app/auth/contextOriginService'
import { HEADER_REGGORA_ORIGIN } from '@config/headers'
import { tokenRefreshService } from '@app/auth/token-refresh-service'

export const API_VERSION = {
  TWO: 2,
  SERVICE: 'service',
}

export const REQUEST_METHOD = {
  POST: 'POST',
  GET: 'GET',
  PUT: 'PUT',
  PATCH: 'PATCH',
  OPTIONS: 'OPTIONS',
  DELETE: 'DELETE',
}

export const getBaseUrl = (version, service_url_override) => {
  let api = API2_URL
  if (version === API_VERSION.SERVICE) {
    api = SERVICE_URL
    if (service_url_override) {
      api = service_url_override
    }
  }
  return api
}

export default function(endpoint, apiVersion = API_VERSION.TWO, service_url_override = '') {
  return new API(endpoint, apiVersion, service_url_override)
}

function deepValues(obj) {
  // creates flat list of all `obj` values (including nested)
  if (isPlainObject(obj) || Array.isArray(obj)) {
    return flatMapDeep(obj, deepValues)
  }
  return obj
}

function hasFile(obj) {
  // check if `obj` has at least one `File` instance
  return deepValues(obj).some((v) => v instanceof Blob)
}

class API {
  constructor(endpoint, apiVersion, service_url_override = '') {
    const validVersions = [API_VERSION.TWO, API_VERSION.SERVICE]
    if (!validVersions.includes(apiVersion)) {
      console.error(`invalid API version: ${apiVersion}. Only APIs 2 and service are supported.`)
    }
    this.endpoint = endpoint
    this.version = apiVersion
    this.service_url_override = service_url_override
    this.reggora_origin_context = contextOriginService.getToken()
  }

  async getAuthorizationHeader() {
    const token = await tokenService.getTokenAsync()
    const prefix = 'bearer'
    return token ? `${prefix} ${token}` : ''
  }

  prepareBody(body, isMultipartFormData) {
    if (isEmpty(body)) {
      return body
    }

    if (isPlainObject(body)) {
      // FIXME we shouldn't send file object represented by url
      ;['avatar', 'logo', 'file'].forEach((field) => isString(body[field]) && delete body[field])
    }

    if (isMultipartFormData) {
      const formData = new FormData()
      for (var name in body) {
        if (isFunction(body[name])) {
          // FIXME there should not be functions
          console.warn('API detects invalid data value (function) in field:', name)
          continue
        } else if (Array.isArray(body[name])) {
          body[name].forEach((value, i) => {
            if (isObject(value) && !(value instanceof Blob)) {
              keys(value).forEach((key) => {
                formData.append(`${name}[${i}]${key}`, value[key])
              })
            } else {
              formData.append(name, value)
            }
          })
        } else if (isPlainObject(body[name])) {
          keys(body[name]).forEach((key) => {
            formData.append(`${name}.${key}`, body[name][key])
          })
        } else {
          if (body[name] !== null) {
            formData.append(name, body[name])
          }
        }
      }
      return formData
    } else {
      return JSON.stringify(body)
    }
  }

  rejectWithReadableResponse(response) {
    response.toString = () => `HTTP ${response.status} ${response.statusText} (${response.url})`
    return Promise.reject(response)
  }

  async handleResponseCallback(response) {
    if (response.status === 204) {
      // 204 (No Content)
      return Promise.resolve({})
    } else if (response.status === 500) {
      return this.rejectWithReadableResponse(response)
    } else if (response.status === 404) {
      // 404 (Not Found) - resolve without parsing body to avoid console errors when body is empty or non-JSON
      return Promise.resolve({ result: null })
    }

    const contentType = response.headers.get('Content-Type')

    if (contentType && contentType === 'application/octet-stream') {
      if (response.ok) {
        const filename = response.headers
          .get('Content-Disposition')
          .split('filename=')[1]
          .substring(1, response.headers.get('Content-Disposition').split('filename=')[1].length - 1)
        const blob = await response.blob()
        return { blob: blob, filename: filename }
      } else {
        return this.rejectWithReadableResponse(response)
      }
    }

    if (response.headers.get('Content-Type') !== 'application/json') {
      return this.rejectWithReadableResponse(response)
    }

    return response.json().then(function(body) {
      if (response.ok) {
        return body
      }
      // handle errors
      var errors = {
        status: response.status,
      }
      keys(body).forEach((key) => {
        let eKey = key
        if (key === 'non_field_errors' || key === 'nonFieldErrors' || key === 'detail') {
          eKey = '_error'
        }
        if (Array.isArray(body[key])) {
          errors[eKey] = body[key][0]
        } else {
          errors[eKey] = body[key]
        }
      })
      return Promise.reject(errors)
    })
  }

  async handleStreamResponse(response) {
    if (!response.ok) {
      return Promise.reject(response)
    }

    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''
    const items = []

    async function readStream() {
      while (true) {
        const { value, done } = await reader.read()
        if (done) {
          break
        }

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')

        // Keep the last incomplete line in buffer
        buffer = lines.pop() || ''

        for (const line of lines) {
          try {
            if (line.trim()) {
              items.push(JSON.parse(line))
            }
          } catch (err) {
            console.error('Error parsing line:', line, err)
          }
        }
      }

      // Handle any remaining buffer content
      if (buffer.trim()) {
        try {
          items.push(JSON.parse(buffer))
        } catch (err) {
          console.error('Error parsing last buffer:', buffer, err)
        }
      }
    }

    await readStream()
    return items
  }

  async request(method, params = {}, body = {}, isStreaming = false) {
    // choose api url, defaulting to api_v2 on lender side
    const api = getBaseUrl(this.version, this.service_url_override)

    const queryParams = isEmpty(params) ? '' : '?' + buildQueryParams(params)
    let resource = `${api}${this.endpoint}`
    if (this.version === 1 && Object.keys(queryParams).length > 1) {
      resource = `${resource}/${queryParams}`
    } else if (Object.keys(queryParams).length > 1) {
      resource = `${resource}${queryParams}`
    }
    const authorizationHeader = await this.getAuthorizationHeader()
    const headers = new Headers({
      Authorization: authorizationHeader,
      'Content-Type': 'application/json',
    })
    if (this.reggora_origin_context) {
      headers.append(HEADER_REGGORA_ORIGIN, this.reggora_origin_context)
    }

    const isMultipartFormData = hasFile(body)
    isMultipartFormData && headers.delete('Content-Type')

    const options = {
      method,
      headers,
    }

    if (method !== REQUEST_METHOD.GET) {
      options.body = this.prepareBody(body, isMultipartFormData)
    }

    const responseHandler = isStreaming
      ? this.handleStreamResponse.bind(this)
      : this.handleResponseCallback.bind(this)

    const response = await fetch(resource, options)

    if (response.status === 401) {
      try {
        await tokenRefreshService.attemptRefresh()
      } catch (refreshError) {
        return responseHandler(response)
      }

      const newToken = tokenService.getToken()
      if (newToken) {
        options.headers.set('Authorization', `bearer ${newToken}`)
      }
      const retryResponse = await fetch(resource, options)
      return responseHandler(retryResponse)
    }

    return responseHandler(response)
  }

  post(body = {}, params = {}) {
    return this.request(REQUEST_METHOD.POST, params, body)
  }

  get(params) {
    return this.request(REQUEST_METHOD.GET, params)
  }

  put(body = {}, params = {}) {
    return this.request(REQUEST_METHOD.PUT, params, body)
  }

  patch(body = {}, params = {}) {
    return this.request(REQUEST_METHOD.PATCH, params, body)
  }

  options() {
    return this.request(REQUEST_METHOD.OPTIONS)
  }

  delete() {
    return this.request(REQUEST_METHOD.DELETE)
  }
}
